import { useState, useEffect } from 'react';
import { Bot, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu.js';
import { useAuth } from '@/contexts/AuthContext.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useModelFavoritesStore } from '@/stores/modelFavoritesStore.js';
import { cn } from '@/lib/utils.js';

// Provider logo mapping
const PROVIDER_LOGOS: Record<string, string> = {
  openai: '/logos/openai.svg',
  anthropic: '/logos/claude-color.svg',
  google: '/logos/gemini-color.svg',
  groq: '/logos/groq.svg',
  xai: '/logos/grok.svg',
  'openai-compatible': '/logos/openai.svg',
  cohere: '/logos/cohere-color.svg',
  ollama: '/logos/ollama.svg',
};

// Logos that have hardcoded colors and don't need dark mode inversion
const COLORED_LOGOS = ['google', 'cohere', 'anthropic'];

const needsDarkModeInversion = (provider: string): boolean => {
  return !COLORED_LOGOS.includes(provider);
};


interface CurrentLLM {
  provider: string;
  model: string;
  displayName?: string;
  router?: string;
}

interface FavoriteModel {
  provider: string;
  model: string;
  displayName?: string;
  providerName?: string;
}

export function ModelPicker() {
  const { api } = useAuth();
  const currentSessionId = useSessionStore((state) => state.currentSessionId);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentLLM, setCurrentLLM] = useState<CurrentLLM | null>(null);
  const [favoriteModels, setFavoriteModels] = useState<FavoriteModel[]>([]);
  const { favorites } = useModelFavoritesStore();

  // Load current LLM
  useEffect(() => {
    const loadCurrentLLM = async () => {
      try {
        const response = await api.getCurrentLLM(currentSessionId || undefined);
        setCurrentLLM(response.config);
      } catch (error) {
        console.error('Failed to load current LLM:', error);
      }
    };

    // Load current LLM always (with or without session)
    loadCurrentLLM();
  }, [api, currentSessionId]);

  // Load favorite models details when opening dropdown
  useEffect(() => {
    const loadFavoriteModels = async () => {
      if (!open || favorites.length === 0) return;

      setLoading(true);
      try {
        // Load catalog to get model details
        const response = await api.getLLMCatalog('grouped');
        const providers = response.providers || {};

        // Map favorites to full model info
        const favModels: FavoriteModel[] = [];

        for (const fav of favorites) {
          const provider = providers[fav.provider];
          if (!provider) continue;

          const model = provider.models.find((m: any) => m.name === fav.model);
          if (!model) continue;

          favModels.push({
            provider: fav.provider,
            model: fav.model,
            displayName: model.displayName || model.name,
            providerName: provider.name,
          });
        }

        setFavoriteModels(favModels);
      } catch (error) {
        console.error('Failed to load favorite models:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFavoriteModels();
  }, [open, favorites, api]);

  const handleModelSelect = async (providerId: string, modelName: string) => {
    try {
      const payload: any = {
        provider: providerId,
        model: modelName,
      };

      // Use router from current config if available
      if (currentLLM?.router) {
        payload.router = currentLLM.router;
      }

      // Add session ID if we have one
      if (currentSessionId) {
        payload.sessionId = currentSessionId;
      }

      await api.switchLLM(payload);

      // Refresh current LLM
      const response = await api.getCurrentLLM(currentSessionId || undefined);
      setCurrentLLM(response.config);

      setOpen(false);
    } catch (error) {
      console.error('Failed to switch model:', error);
    }
  };


  // Always show the current model or loading state
  const triggerLabel = loading
    ? 'Loading...'
    : currentLLM?.displayName || currentLLM?.model || 'Select Model';
  const currentProviderLogo = currentLLM?.provider ? PROVIDER_LOGOS[currentLLM.provider] : null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-1.5 cursor-pointer h-8 px-2"
          title="Choose model"
        >
          {currentProviderLogo ? (
            <img
              src={currentProviderLogo}
              alt={currentLLM?.provider}
              className={cn(
                'h-4 w-4 object-contain',
                needsDarkModeInversion(currentLLM?.provider || '') &&
                  'dark:invert dark:brightness-0 dark:contrast-200'
              )}
            />
          ) : (
            <Bot className="h-3.5 w-3.5" />
          )}
          <span className="text-xs">{triggerLabel}</span>
          <ChevronDown
            className={cn('h-3 w-3 transition-transform', open && 'rotate-180')}
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-[280px] max-h-[400px] overflow-y-auto"
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : favoriteModels.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              No models enabled
            </p>
            <p className="text-xs text-muted-foreground">
              Open Settings to enable models
            </p>
          </div>
        ) : (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Available Models
            </DropdownMenuLabel>
            {favoriteModels.map((model) => {
              const isActive =
                currentLLM?.provider === model.provider &&
                currentLLM?.model === model.model;

              const providerLogo = PROVIDER_LOGOS[model.provider];

              return (
                <DropdownMenuItem
                  key={`${model.provider}:${model.model}`}
                  onClick={() => handleModelSelect(model.provider, model.model)}
                  className={cn(
                    'cursor-pointer flex items-center gap-2',
                    isActive && 'bg-primary/10 text-primary font-medium'
                  )}
                >
                  {providerLogo ? (
                    <img
                      src={providerLogo}
                      alt={model.provider}
                      className={cn(
                        'h-4 w-4 object-contain flex-shrink-0',
                        needsDarkModeInversion(model.provider) &&
                          'dark:invert dark:brightness-0 dark:contrast-200'
                      )}
                    />
                  ) : (
                    <Bot className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="flex-1 truncate">{model.displayName}</span>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
